/**
 * Build identity for the admin panel (subtle version stamp).
 * Populated at Vite build via vite.config define (Railway / Vercel git SHA).
 */

export type AdminPanelVersion = {
  /** Short SHA (7) or empty */
  shortSha: string;
  /** Full SHA if known */
  fullSha: string;
  /** production | preview | development | railway env name */
  env: string;
  /** package.json version when available */
  packageVersion: string;
  /** One-line label for UI, e.g. "v0 · a1b2c3d · production" */
  label: string;
  /** Longer title attribute for hover */
  title: string;
};

function readSha(): string {
  try {
    return String(import.meta.env.VITE_GIT_COMMIT_SHA || "").trim();
  } catch {
    return "";
  }
}

function readEnv(): string {
  try {
    const e = String(import.meta.env.VITE_DEPLOY_ENV || import.meta.env.MODE || "").trim();
    return e || (import.meta.env.DEV ? "development" : "unknown");
  } catch {
    return import.meta.env.DEV ? "development" : "unknown";
  }
}

/** package.json version is often 0.0.0 — still useful as product stamp */
function readPackageVersion(): string {
  try {
    // Prefer explicit build inject if added later
    const injected = String(
      (import.meta.env as { VITE_APP_VERSION?: string }).VITE_APP_VERSION || "",
    ).trim();
    if (injected) return injected;
  } catch {
    /* ignore */
  }
  return "0";
}

let cached: AdminPanelVersion | null = null;

export function getAdminPanelVersion(): AdminPanelVersion {
  if (cached) return cached;
  const fullSha = readSha();
  const shortSha = fullSha ? fullSha.slice(0, 7) : import.meta.env.DEV ? "dev" : "—";
  const env = readEnv();
  const packageVersion = readPackageVersion();
  const parts = [
    packageVersion && packageVersion !== "0.0.0" ? `v${packageVersion}` : null,
    shortSha !== "—" ? shortSha : null,
    env && env !== "unknown" ? env : null,
  ].filter(Boolean);
  const label = parts.length ? parts.join(" · ") : "admin";
  const title = [
    "Panel admin",
    fullSha ? `commit ${fullSha}` : "commit local / sin SHA de build",
    `env ${env}`,
  ].join(" · ");
  cached = { shortSha, fullSha, env, packageVersion, label, title };
  return cached;
}
